import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import {
  Image as ImageIcon,
  Upload,
  Loader2,
  RefreshCw,
  Check,
  Save,
} from 'lucide-react';
import { siteImagesAPI, mediaAPI, API_BASE } from '../../lib/api';
import { FocalPointPicker } from '../../components/shared/FocalPointPicker';
import { toast } from 'sonner';

const DEFAULT_IMAGES = [
  { key: 'homepage-hero', name: 'Hero da Homepage', section: 'homepage', hint: 'Ideal: 1920x800px (panorâmica)' },
  { key: 'homepage-about', name: 'Seção Sobre na Homepage', section: 'homepage', hint: 'Ideal: 800x600px' },
  { key: 'about-hero', name: 'Hero da Página Sobre', section: 'about', hint: 'Ideal: 1920x800px (panorâmica)' },
  { key: 'about-team', name: 'Equipe na Página Sobre', section: 'about', hint: 'Ideal: 800x600px' },
];

const POSITIONS = [
  { value: 'top', label: 'Topo' },
  { value: 'center', label: 'Centro' },
  { value: 'bottom', label: 'Base' },
  { value: 'left', label: 'Esquerda' },
  { value: 'right', label: 'Direita' },
];

const FIT_MODES = [
  { value: 'cover', label: 'Preencher (corta para caber)' },
  { value: 'contain', label: 'Encaixar (mostra inteira)' },
];

const AdminSiteImages = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingFiles, setPendingFiles] = useState({});
  const [uploadingKey, setUploadingKey] = useState(null);
  const [savedKeys, setSavedKeys] = useState(new Set());

  useEffect(() => {
    loadImages();
  }, []);

  const loadImages = async () => {
    setLoading(true);
    try {
      await siteImagesAPI.init().catch(() => {});
      const response = await siteImagesAPI.getAll();
      const imagesMap = {};
      response.data.forEach(img => { imagesMap[img.key] = img; });

      const merged = DEFAULT_IMAGES.map(def => ({
        ...def,
        url: imagesMap[def.key]?.url || '',
        position: imagesMap[def.key]?.position || 'center',
        fit_mode: imagesMap[def.key]?.fit_mode || 'cover',
      }));
      setImages(merged);
    } catch (error) {
      toast.error('Erro ao carregar imagens');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setPendingFiles(prev => ({ ...prev, [key]: { file, previewUrl } }));
    setSavedKeys(prev => { const n = new Set(prev); n.delete(key); return n; });
    e.target.value = '';
  };

  const handleSaveImage = async (key) => {
    const pending = pendingFiles[key];
    if (!pending) return;

    setUploadingKey(key);
    try {
      const response = await mediaAPI.upload(pending.file);
      const mediaUrl = `/api/media/${response.data.id}`;
      const img = images.find(i => i.key === key);
      await siteImagesAPI.update(key, {
        url: mediaUrl,
        name: img?.name || key,
        position: img?.position || 'center',
        fit_mode: img?.fit_mode || 'cover',
      });

      URL.revokeObjectURL(pending.previewUrl);
      setPendingFiles(prev => { const n = { ...prev }; delete n[key]; return n; });
      setSavedKeys(prev => new Set(prev).add(key));
      toast.success('Imagem salva com sucesso!');
      loadImages();
    } catch (error) {
      toast.error('Erro ao salvar imagem');
    } finally {
      setUploadingKey(null);
    }
  };

  const handleSettingChange = async (key, field, value) => {
    setImages(prev => prev.map(img =>
      img.key === key ? { ...img, [field]: value } : img
    ));
    try {
      await siteImagesAPI.updateSettings(key, { [field]: value });
      toast.success('Atualizado!');
    } catch (error) {
      toast.error('Erro ao salvar');
    }
  };

  const handlePositionDrag = (key, value) => {
    setImages(prev => prev.map(img =>
      img.key === key ? { ...img, position: value } : img
    ));
  };

  const handlePositionCommit = async (key, value) => {
    try {
      await siteImagesAPI.updateSettings(key, { position: value });
      toast.success('Posição salva!');
    } catch (error) {
      toast.error('Erro ao salvar posição');
    }
  };

  const groupedImages = images.reduce((acc, img) => {
    if (!acc[img.section]) acc[img.section] = [];
    acc[img.section].push(img);
    return acc;
  }, {});

  const sectionNames = { homepage: 'Página Inicial', about: 'Página Sobre' };

  return (
    <div data-testid="admin-site-images">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Imagens do Site</h1>
          <p className="text-slate-600">Gerencie as imagens exibidas nas páginas do site</p>
        </div>
        <Button variant="outline" onClick={loadImages} disabled={loading} className="btn-secondary">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2].map(i => <div key={i} className="skeleton h-48 rounded-xl" />)}
        </div>
      ) : (
        <div className="space-y-10">
          {Object.entries(groupedImages).map(([section, sectionImages]) => (
            <div key={section}>
              <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                {sectionNames[section] || section}
              </h2>
              <div className="space-y-6">
                {sectionImages.map((image) => {
                  const pending = pendingFiles[image.key];
                  const displayUrl = pending ? pending.previewUrl : image.url;

                  return (
                    <Card key={image.key} data-testid={`site-image-${image.key}`}>
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-semibold text-slate-900">{image.name}</h3>
                            <p className="text-xs text-slate-500">{image.hint}</p>
                          </div>
                          {pending && (
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-medium">
                              Pendente
                            </span>
                          )}
                        </div>

                        {/* Image Preview */}
                        <div
                          className="w-full h-52 rounded-lg bg-slate-100 overflow-hidden border border-slate-200 mb-4"
                        >
                          {displayUrl ? (
                            !pending && image.url && (image.fit_mode || 'cover') === 'cover' ? (
                              <FocalPointPicker
                                src={displayUrl}
                                value={image.position || 'center'}
                                onChange={(v) => handlePositionDrag(image.key, v)}
                                onCommit={(v) => handlePositionCommit(image.key, v)}
                                className="w-full h-full"
                              />
                            ) : (
                              <img
                                src={displayUrl}
                                alt={image.name}
                                className="w-full h-full"
                                style={{
                                  objectFit: image.fit_mode || 'cover',
                                  objectPosition: image.position || 'center',
                                }}
                              />
                            )
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                              <ImageIcon className="w-10 h-10 text-slate-300" />
                              <span className="text-sm text-slate-400">Nenhuma imagem</span>
                            </div>
                          )}
                        </div>

                        {/* Upload Controls */}
                        <div className="flex items-center gap-2 mb-4">
                          <input
                            type="file"
                            id={`upload-${image.key}`}
                            accept="image/*"
                            onChange={(e) => handleFileSelect(e, image.key)}
                            className="hidden"
                          />
                          <label htmlFor={`upload-${image.key}`}>
                            <Button asChild variant="outline" size="sm" className="cursor-pointer">
                              <span>
                                <Upload className="w-4 h-4 mr-2" />
                                {pending ? 'Escolher outra' : 'Escolher imagem'}
                              </span>
                            </Button>
                          </label>
                          {pending && (
                            <Button
                              onClick={() => handleSaveImage(image.key)}
                              disabled={uploadingKey === image.key}
                              size="sm"
                              className={savedKeys.has(image.key) ? 'bg-green-600 hover:bg-green-700' : 'btn-primary'}
                            >
                              {uploadingKey === image.key ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-1" />
                              ) : savedKeys.has(image.key) ? (
                                <Check className="w-4 h-4 mr-1" />
                              ) : (
                                <Save className="w-4 h-4 mr-1" />
                              )}
                              {uploadingKey === image.key ? 'Salvando...' : 'Salvar imagem'}
                            </Button>
                          )}
                        </div>

                        {/* Position & Fit Controls - only when image exists */}
                        {image.url && (
                          <div className="border-t border-slate-100 pt-4 space-y-3">
                            <div className="flex items-center justify-between gap-2">
                              <Label className="text-xs text-slate-500">
                                {(image.fit_mode || 'cover') === 'cover'
                                  ? 'Arraste a imagem na pré-visualização para escolher exatamente a parte visível'
                                  : 'No modo "Encaixar" a imagem inteira é exibida (sem posicionamento)'}
                              </Label>
                              {(image.fit_mode || 'cover') === 'cover' && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    handlePositionDrag(image.key, '50% 50%');
                                    handlePositionCommit(image.key, '50% 50%');
                                  }}
                                  className="text-xs text-[#1e4e7e] hover:underline flex-shrink-0"
                                  data-testid={`reset-position-${image.key}`}
                                >
                                  Centralizar
                                </button>
                              )}
                            </div>
                            <div>
                              <Label className="text-xs text-slate-500 mb-1.5 block">Modo de exibicao</Label>
                              <div className="flex flex-wrap gap-1.5">
                                {FIT_MODES.map((mode) => (
                                  <button
                                    key={mode.value}
                                    type="button"
                                    onClick={() => handleSettingChange(image.key, 'fit_mode', mode.value)}
                                    className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                                      image.fit_mode === mode.value
                                        ? 'bg-[#1e4e7e] text-white border-[#1e4e7e]'
                                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                                    }`}
                                  >
                                    {mode.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminSiteImages;
